<script>
  import { onDestroy, onMount, tick } from "svelte";
  import { get } from "svelte/store";
  import { LngLatBounds, Popup } from "maplibre-gl";
  import {
    updateSelectionFilters,
    stationsToGeoJSON,
    debounce,
    getStationBounds,
    DEFAULT_VIEW
  } from "./mapUtils.js";
  import {
    selectedStations,
    highlightedStation,
    focusedStation,
    cameraRequest,
    mapDisplayStations
  } from "../../services/station-service.ts";
  import StationPopup from "./StationPopup.svelte";

  export let map;
  export let displayStations = [];

  const ZOOM_LEVELS = {
    SELECTED_STATION: 10,
    CLUSTER_MAX: 12
  };
  const POPUP_DELAY = 300;
  const MAP_UPDATE_DELAY = 100;
  const FLY_DURATION = 800;
  const FIT_PADDING = 60;
  const FIT_MAX_ZOOM = 12;
  const POPUP_CONFIG = {
    HOVER: { maxWidth: "200px", closeButton: false, anchor: "bottom", offset: [0, -10] },
    VIEWING: { maxWidth: "300px", closeButton: true, anchor: "bottom", offset: [0, -10] }
  };

  let currentPopup = null;
  let popupComponent = null;
  let viewingStationId = null;
  let isInitialized = false;
  let initializationPromise = null;
  let hoveredStateId = null;
  let pendingFocus = null;

  $: if (map && map.getLayer("selected-stations")) {
    updateSelectionFilters(map, $selectedStations);
  }

  const debouncedLayerUpdate = debounce(() => {
    if (isInitialized && map?.getSource("stations") && displayStations) {
      const source = map.getSource("stations");
      if (source) {
        source.setData(stationsToGeoJSON(displayStations));
      }
    }
  }, MAP_UPDATE_DELAY);

  $: if (isInitialized && displayStations) {
    debouncedLayerUpdate();
  }

  function showPopup(station, isHover = false) {
    closePopup();
    const container = document.createElement("div");
    const config = isHover ? POPUP_CONFIG.HOVER : POPUP_CONFIG.VIEWING;
    popupComponent = new StationPopup({
      target: container,
      props: { station, isHover, isViewing: !isHover }
    });
    currentPopup = new Popup({ ...config, closeOnClick: false, closeOnEsc: true })
      .setDOMContent(container)
      .setLngLat([station.longitude, station.latitude])
      .addTo(map);
    currentPopup.on("close", () => {
      if (popupComponent) {
        popupComponent.$destroy();
        popupComponent = null;
      }
      if (!isHover) viewingStationId = null;
    });
  }

  function closePopup() {
    if (popupComponent) {
      popupComponent.$destroy();
      popupComponent = null;
    }
    if (currentPopup) {
      currentPopup.remove();
      currentPopup = null;
    }
  }

  export function closeAllPopups() {
    closePopup();
    viewingStationId = null;
  }

  function selectStation(station) {
    viewingStationId = station.id;
    highlightedStation.set(station.id);
    map.flyTo({
      center: [station.longitude, station.latitude],
      zoom: Math.max(map.getZoom(), ZOOM_LEVELS.SELECTED_STATION),
      duration: FLY_DURATION
    });
    setTimeout(() => showPopup(station, false), POPUP_DELAY);
  }

  // Leave the camera alone if the user can already see results.
  function resultsInView(stations) {
    const camera = map.getBounds();
    return stations.some(
      (s) =>
        Number.isFinite(s?.longitude) &&
        Number.isFinite(s?.latitude) &&
        camera.contains([s.longitude, s.latitude])
    );
  }

  // Read the store, not the prop; the prop hasn't flushed yet when this fires.
  function fitToResults() {
    const stations = get(mapDisplayStations);
    if (!stations.length || resultsInView(stations)) return;

    const b = getStationBounds(stations);
    if (!b) return;

    map.fitBounds(new LngLatBounds([b.west, b.south], [b.east, b.north]), {
      padding: FIT_PADDING,
      maxZoom: FIT_MAX_ZOOM,
      duration: FLY_DURATION
    });
  }

  onMount(() => {
    const unsubFocus = focusedStation.subscribe((station) => {
      if (!station) return;
      focusedStation.set(null);
      if (isInitialized) {
        selectStation(station);
      } else {
        pendingFocus = station;
      }
    });

    const unsubCamera = cameraRequest.subscribe((request) => {
      if (!request || !map) return;
      closeAllPopups();
      if (request.kind === "home") {
        map.easeTo({ ...DEFAULT_VIEW, duration: FLY_DURATION });
      } else {
        fitToResults();
      }
    });

    return () => {
      unsubFocus();
      unsubCamera();
    };
  });

  async function setupMapLayers() {
    const geojsonData = stationsToGeoJSON(displayStations);
    if (map.getSource("stations")) {
      map.getSource("stations").setData(geojsonData);
    } else {
      map.addSource("stations", {
        type: "geojson",
        data: geojsonData,
        cluster: true,
        clusterMaxZoom: ZOOM_LEVELS.CLUSTER_MAX,
        clusterRadius: 50
      });
      addMapLayers();
      setupMapInteractions();
      updateSelectionFilters(map, get(selectedStations));
    }
  }

  function addMapLayers() {
    const layers = [
      {
        id: "clusters",
        type: "circle",
        source: "stations",
        filter: ["has", "point_count"],
        paint: {
          "circle-color": "#1976D2",
          "circle-radius": ["step", ["get", "point_count"], 20, 100, 30, 750, 40],
          "circle-stroke-width": 2,
          "circle-stroke-color": "#fff"
        }
      },
      {
        id: "cluster-count",
        type: "symbol",
        source: "stations",
        filter: ["has", "point_count"],
        layout: {
          "text-field": "{point_count_abbreviated}",
          "text-size": 12,
          "text-font": ["DIN Pro Medium", "Arial Unicode MS Bold"]
        },
        paint: { "text-color": "#ffffff" }
      },
      {
        id: "stations",
        type: "circle",
        source: "stations",
        filter: ["!", ["has", "point_count"]],
        paint: {
          "circle-radius": ["case", ["boolean", ["feature-state", "hover"], false], 10, 8],
          "circle-color": [
            "case",
            ["boolean", ["feature-state", "hover"], false],
            "#0D47A1",
            "#1976D2"
          ],
          "circle-stroke-width": ["case", ["boolean", ["feature-state", "hover"], false], 2, 1.5],
          "circle-stroke-color": "#FFFFFF"
        }
      },
      {
        id: "selected-stations",
        type: "circle",
        source: "stations",
        filter: ["in", ["get", "id"], ["literal", []]],
        paint: {
          "circle-radius": 10,
          "circle-color": "#c9520f",
          "circle-stroke-width": 2,
          "circle-stroke-color": "#FFFFFF"
        }
      },
      {
        id: "station-labels",
        type: "symbol",
        source: "stations",
        filter: ["!", ["has", "point_count"]],
        layout: {
          "text-field": ["get", "name"],
          "text-size": 11,
          "text-offset": [0, 1.5],
          "text-anchor": "top",
          "text-font": ["DIN Pro Medium", "Arial Unicode MS Bold"]
        },
        paint: {
          "text-color": "#333",
          "text-halo-color": "#fff",
          "text-halo-width": 1
        },
        minzoom: 8
      },
      {
        id: "selected-station-labels",
        type: "symbol",
        source: "stations",
        filter: ["in", ["get", "id"], ["literal", []]],
        layout: {
          "text-field": ["get", "name"],
          "text-size": 12,
          "text-offset": [0, 1.8],
          "text-anchor": "top",
          "text-font": ["DIN Pro Bold", "Arial Unicode MS Bold"]
        },
        paint: {
          "text-color": "#c9520f",
          "text-halo-color": "#fff",
          "text-halo-width": 1.5
        },
        minzoom: 6
      }
    ];

    layers.forEach((layer) => {
      if (!map.getLayer(layer.id)) map.addLayer(layer);
    });
  }

  function setupMapInteractions() {
    map.on("mouseenter", "clusters", () => {
      map.getCanvas().style.cursor = "pointer";
    });

    map.on("mouseleave", "clusters", () => {
      map.getCanvas().style.cursor = "";
    });

    map.on("mouseenter", "stations", (e) => {
      map.getCanvas().style.cursor = "pointer";

      if (e.features.length > 0) {
        if (hoveredStateId !== null) {
          map.setFeatureState({ source: "stations", id: hoveredStateId }, { hover: false });
        }
        hoveredStateId = e.features[0].id;
        map.setFeatureState({ source: "stations", id: hoveredStateId }, { hover: true });
      }
    });

    map.on("mouseleave", "stations", () => {
      map.getCanvas().style.cursor = "";

      if (hoveredStateId !== null) {
        map.setFeatureState({ source: "stations", id: hoveredStateId }, { hover: false });
      }
      hoveredStateId = null;
    });

    function isDrawing() {
      return map._drawJustFinished === true;
    }

    map.on("click", "clusters", (e) => {
      if (isDrawing()) return;
      const features = map.queryRenderedFeatures(e.point, { layers: ["clusters"] });
      if (!features.length) return;
      const clusterId = features[0].properties.cluster_id;
      map.getSource("stations").getClusterExpansionZoom(clusterId, (err, zoom) => {
        if (err) return;
        map.easeTo({
          center: features[0].geometry.coordinates,
          zoom: zoom + 1
        });
      });
    });

    map.on("click", "stations", (e) => {
      if (isDrawing()) return;
      const features = map.queryRenderedFeatures(e.point, { layers: ["stations"] });
      if (!features.length) return;
      const stationId = features[0].properties.id;
      if (stationId) {
        const station = displayStations.find((s) => s.id === stationId);
        if (station) selectStation(station);
      }
    });
  }

  export async function initialize() {
    if (!map || !map.isStyleLoaded()) {
      await new Promise((resolve) => map.once("styledata", resolve));
    }
    await tick();
    isInitialized = true;
    await setupMapLayers();
    if (pendingFocus) {
      selectStation(pendingFocus);
      pendingFocus = null;
    }
  }

  $: if (map && map.isStyleLoaded() && !isInitialized && displayStations) {
    initialize();
  }

  onDestroy(() => {
    if (hoveredStateId !== null && map) {
      map.setFeatureState({ source: "stations", id: hoveredStateId }, { hover: false });
    }
  });
</script>

<style>
  :global(.maplibregl-popup-content) {
    padding: 12px;
    border-radius: 8px;
    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  }
</style>